import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  CaseEditorStepFrame,
  caseManagerInsetSurfaceSx,
  publicBodyFontFamily,
  publicSecondaryButtonSx,
} from '../caseManagerStyles';

const ScoringStep = ({
  scoringConfig,
  scoringFields,
  excludedScoringFields = [],
  effectiveDefaults = {},
  scoringTotal,
  scoringIsBalanced,
  onScoringChange,
  onResetScoring,
  formatWeightPercent,
}) => {
  const [editingKey, setEditingKey] = useState(null);
  const [editingValue, setEditingValue] = useState('');

  const resolvedWeights = scoringConfig?.resolved || {};
  const hasScoringOverrides = Boolean(scoringConfig?.overrides);

  return (
    <CaseEditorStepFrame>
      <Paper sx={{ ...caseManagerInsetSurfaceSx, p: 3, mb: 4 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={2}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Scoring Configuration
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: publicBodyFontFamily }}>
              Weight how each part of this case contributes to the overall score. Only the parts this case
              marks are included, and their weights must total 100% before saving.
            </Typography>
          </Box>
          <Button
            disabled={!hasScoringOverrides || !scoringFields.length}
            onClick={onResetScoring}
            sx={publicSecondaryButtonSx}
          >
            Reset to defaults
          </Button>
        </Box>

        {excludedScoringFields.length ? (
          <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
            Not scored for this case: {excludedScoringFields.map((field) => field.label).join(', ')}.
            Flow and Empathy count only when the mark scheme has a Flow or Empathy section, and examiner
            questions count only when the case has at least one marked question.
          </Alert>
        ) : null}

        {!scoringConfig ? (
          <Box display="flex" alignItems="center" gap={2}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">
              Loading scoring configuration…
            </Typography>
          </Box>
        ) : !scoringFields.length ? (
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            Add marked items to the mark scheme before configuring scoring weights.
          </Alert>
        ) : (
          <>
            <Stack spacing={3}>
              {scoringFields.map((field) => {
                const defaultValue = effectiveDefaults[field.key] ?? 0;
                const resolvedValue = resolvedWeights[field.key] ?? defaultValue;
                const isOverride = Math.abs(resolvedValue - defaultValue) > 0.0005;

                return (
                  <Paper
                    key={field.key}
                    variant="outlined"
                    sx={{
                      p: 2.5,
                      borderRadius: 2,
                      bgcolor: 'grey.50',
                    }}
                  >
                    <Grid container alignItems="center" spacing={2}>
                      <Grid item xs={12} md={8}>
                        <Typography variant="subtitle1" fontWeight="600">
                          {field.label}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {field.description}
                        </Typography>
                        <Chip
                          size="small"
                          variant={isOverride ? 'filled' : 'outlined'}
                          color={isOverride ? 'warning' : 'default'}
                          label={`Default ${formatWeightPercent(defaultValue)}%`}
                          sx={{ mt: 1 }}
                        />
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <TextField
                          type="number"
                          label="Weight (%)"
                          fullWidth
                          size="small"
                          value={
                            editingKey === field.key
                              ? editingValue
                              : formatWeightPercent(resolvedValue)
                          }
                          inputProps={{ min: 0, max: 100, step: 1, 'aria-label': `${field.label} weight` }}
                          onFocus={() => {
                            setEditingKey(field.key);
                            setEditingValue(formatWeightPercent(resolvedValue));
                          }}
                          onChange={(e) => {
                            const raw = e.target.value;
                            setEditingValue(raw);
                            if (raw === '' || raw === '-') return; // allow temporary blanks
                            const n = Number(raw);
                            if (!Number.isNaN(n)) onScoringChange(field.key, n);
                          }}
                          onBlur={() => {
                            const n = Number(editingValue);
                            if (!Number.isNaN(n)) onScoringChange(field.key, n);
                            setEditingKey(null);
                            setEditingValue('');
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                );
              })}
            </Stack>

            <Divider sx={{ my: 3 }} />

            <Alert
              severity={scoringIsBalanced ? (hasScoringOverrides ? 'info' : 'success') : 'warning'}
              sx={{
                mt: 2,
                borderRadius: 2,
                fontWeight: 500,
              }}
            >
              Weighted total: {(scoringTotal * 100).toFixed(1)}% —{' '}
              {scoringIsBalanced
                ? hasScoringOverrides
                  ? 'Case-specific overrides are active for this configuration.'
                  : 'Using global default weights.'
                : 'Adjust the weights until the total equals 100%.'}
            </Alert>
          </>
        )}
      </Paper>
    </CaseEditorStepFrame>
  );
};

export default ScoringStep;
